import { hasNativeAudioPlugin } from '@/lib/nativeAudio'

/**
 * Runtime capability sniffs for the audio stack. Kept in one module
 * so the UI and the engine agree on what's possible without each
 * re-implementing the same feature detection.
 */

/** True when `navigator.mediaDevices.getUserMedia` is callable from
 *  JS. False on Mac Catalyst's WKWebView (the custom-scheme origin
 *  hides `navigator.mediaDevices` entirely, even with the
 *  microphone entitlement + NSMicrophoneUsageDescription), and on
 *  SSR. Callers that need a mic stream without native help must
 *  guard on this. */
export function hasWebGetUserMedia(): boolean {
  if (typeof navigator === 'undefined') return false
  const md = navigator.mediaDevices as
    | (MediaDevices & { getUserMedia?: unknown })
    | undefined
  return typeof md?.getUserMedia === 'function'
}

/**
 * True when the runtime can record a sample — either via web
 * `MediaRecorder` or the native Capacitor plugin's `startRecording`.
 * The Monitor toggle still requires `canMonitorInput()` because
 * monitoring lands in a later phase than recording.
 */
export function canCaptureAudioInput(): boolean {
  return hasWebGetUserMedia() || hasNativeAudioPlugin()
}

/**
 * True when the runtime can stream live input into the Web Audio
 * graph for monitoring. On web this is `getUserMedia`; on native
 * we stream PCM frames through the BusswerksAudio plugin and
 * re-emit them inside the graph via an AudioWorkletNode so bus FX
 * apply identically (P3 native-input bridge).
 */
export function canMonitorInput(): boolean {
  return hasWebGetUserMedia() || hasNativeAudioPlugin()
}

/** Short explanation for why input capture is unavailable. UI
 *  surfaces this verbatim so users understand WHY Monitor /
 *  per-channel routing are disabled on their current runtime. */
export function audioInputUnavailableReason(): string | null {
  if (canCaptureAudioInput()) return null
  return 'This browser does not expose microphone capture. Monitoring requires Chrome, Edge, Safari, or Firefox with microphone access.'
}

/** Short explanation for why the Monitor button is disabled even
 *  when recording works (Catalyst native recording without the live-
 *  stream bridge). `null` when monitoring is supported. */
export function monitorUnavailableReason(): string | null {
  if (canMonitorInput()) return null
  if (hasNativeAudioPlugin()) {
    return 'Live input monitoring on the Mac / iPad app ships in the next release. Recording to the sample library works today; saved input sources stay configured for when monitoring lands.'
  }
  return 'This browser does not expose microphone capture. Monitoring requires Chrome, Edge, Safari, or Firefox with microphone access.'
}
